import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Phosphor from "@phosphor-icons/react";
import { ArrowRight, MapPin, Check, ArrowDown } from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import StylizedMap from "@/components/StylizedMap";
import { Overline, Container, btn } from "@/components/ui-kit";
import { WHAT_WE_DO, INVESTMENT_FOCUS, WHY_REDWOOD, MARKETS } from "@/lib/content";

const Icon = ({ name, ...props }) => {
  const C = Phosphor[name] || Phosphor.Circle;
  return <C {...props} />;
};

const HOME_IMG = {
  hero: "https://images.unsplash.com/photo-1603298108410-e6f28ad2708d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxhZXJpYWwlMjBzdWJ1cmJhbiUyMHJlc2lkZW50aWFsJTIwbmVpZ2hib3Job29kJTIwZGV2ZWxvcG1lbnR8ZW58MHx8fHwxNzg2NjU3NTk5fDA&ixlib=rb-4.1.0&q=85",
  residential: "https://images.unsplash.com/photo-1571979622878-622d38ec238c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxhZXJpYWwlMjBzdWJ1cmJhbiUyMHJlc2lkZW50aWFsJTIwbmVpZ2hib3Job29kJTIwZGV2ZWxvcG1lbnR8ZW58MHx8fHwxNzg2NjU3NTk5fDA&ixlib=rb-4.1.0&q=85",
  land: "https://images.unsplash.com/photo-1685488919515-5bd4f01b0b93?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxhZXJpYWwlMjB1bmRldmVsb3BlZCUyMGdyZWVuJTIwbGFuZCUyMGZvcmVzdCUyMGZpZWxkc3xlbnwwfHx8fDE3ODY2NTc1OTl8MA&ixlib=rb-4.1.0&q=85",
  development: "https://images.unsplash.com/photo-1773215023063-e662ea91a69c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxuZXclMjByZXNpZGVudGlhbCUyMGNvbnN0cnVjdGlvbiUyMHNpdGUlMjBhZXJpYWwlMjBmcmFtaW5nfGVufDB8fHx8MTc4NjY1NzU5OXww&ixlib=rb-4.1.0&q=85",
  skyline: "https://images.unsplash.com/photo-1562762583-9e1705bcb3ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxDaGFybG90dGUlMjBOb3J0aCUyMENhcm9saW5hJTIwc2t5bGluZSUyMGFlcmlhbHxlbnwwfHx8fDE3ODY2NTc1OTl8MA&ixlib=rb-4.1.0&q=85",
};

const PILLARS = [
  { icon: "MapTrifold", title: "We Acquire Land", text: "Strategically located parcels with residential potential." },
  { icon: "Buildings", title: "We Develop Residential", text: "Ground-up residential land development." },
  { icon: "UsersThree", title: "Owners & Brokers", text: "Direct, discreet conversations with sellers and brokers." },
  { icon: "Handshake", title: "Investors & Partners", text: "Aligned ventures with capital and development professionals." },
  { icon: "MapPin", title: "Charlotte-Focused", text: "The Charlotte metro and high-growth NC markets." },
];

const DEV_POINTS = [
  "Single-family communities & lots",
  "Infill, assemblage & large parcels",
  "Entitlement, zoning & site planning",
  "Builder partnerships & lot sales",
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Charlotte Real Estate Investment & Land Development"
        description="Redwood Capital Holdings identifies, acquires, and develops strategically located land and residential development opportunities across Charlotte and surrounding high-growth North Carolina markets."
        path="/"
        image={HOME_IMG.hero}
      />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="home-hero">
        <motion.img
          src={HOME_IMG.hero}
          alt="Aerial view of a Charlotte-area residential community at golden hour"
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 9, ease: "easeOut" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-forest via-forest/85 to-forest/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest/70 via-transparent to-forest/40" />
        <div className="grain" />
        <Container className="relative py-32">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-gold" />
              <Overline>Real Estate Investment &amp; Development · Charlotte, NC</Overline>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-[4.25rem] font-semibold leading-[1.04] tracking-tight text-white text-balance">
              Creating Value Through Strategic Real Estate Investment &amp; Development
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-white/75 leading-relaxed max-w-2xl">
              Redwood Capital Holdings identifies, acquires, and develops strategically located real estate
              opportunities across Charlotte and surrounding high-growth markets.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/submit-property" className={`${btn.gold} shadow-[0_10px_30px_rgba(201,168,117,0.35)]`} data-testid="hero-submit-btn">
                Submit a Property <ArrowRight size={18} />
              </Link>
              <Link to="/about" className={btn.outlineLight} data-testid="hero-explore-btn">
                Explore Our Business
              </Link>
            </div>
          </motion.div>
        </Container>

        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 hidden md:flex flex-col items-center gap-2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
          <ArrowDown size={16} />
        </motion.div>
      </section>

      {/* POSITIONING PILLARS */}
      <section className="bg-forest text-white border-t border-white/10" data-testid="positioning-pillars">
        <Container className="py-12 md:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div className="h-full bg-forest px-6 py-7 hover:bg-forest-light transition-colors duration-300 group">
                  <Icon name={p.icon} size={26} weight="light" className="text-gold group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="mt-4 font-serif text-base font-semibold text-white leading-snug">{p.title}</h3>
                  <p className="mt-1.5 text-white/55 text-[13px] leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* WHAT WE DO */}
      <section className="py-20 md:py-32" data-testid="what-we-do">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-14">
            <Reveal className="lg:col-span-7">
              <Overline>What We Do</Overline>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-forest tracking-tight">
                A disciplined approach to real estate value creation
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-5">
              <p className="text-ink text-lg leading-relaxed lg:border-l lg:border-line lg:pl-8">
                We combine local market knowledge with rigorous underwriting to acquire, entitle, and develop
                well-located residential land across the Charlotte region.
              </p>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHAT_WE_DO.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="relative h-full bg-white border border-line rounded-sm p-8 overflow-hidden transition-all duration-300 hover:border-gold hover:shadow-[0_18px_44px_rgba(13,46,27,0.10)] hover:-translate-y-1 group">
                  <span className="absolute top-6 right-7 font-serif text-4xl text-forest/5 group-hover:text-gold/25 transition-colors duration-300 select-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="w-12 h-12 flex items-center justify-center rounded-sm bg-forest/5 group-hover:bg-gold/15 transition-colors duration-300">
                    <Icon name={c.icon} size={26} weight="light" className="text-forest" />
                  </div>
                  <h3 className="mt-6 font-serif text-xl font-semibold text-forest">{c.title}</h3>
                  <p className="mt-3 text-ink leading-relaxed text-[15px]">{c.desc}</p>
                  <span className="mt-5 block h-px w-0 bg-gold group-hover:w-12 transition-all duration-500" />
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* RESIDENTIAL DEVELOPMENT — editorial split */}
      <section className="bg-white border-y border-line py-20 md:py-28" data-testid="residential-development">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <Reveal className="lg:col-span-6">
              <div className="relative">
                <div className="rounded-sm overflow-hidden border border-line">
                  <img src={HOME_IMG.residential} alt="Aerial view of a residential community" loading="lazy" className="w-full h-[300px] lg:h-[440px] object-cover" />
                </div>
                <div className="hidden lg:block absolute -bottom-8 -right-6 w-52 rounded-sm overflow-hidden border-4 border-white shadow-xl">
                  <img src={HOME_IMG.development} alt="New residential development site" loading="lazy" className="w-full h-36 object-cover" />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-6">
              <Overline>Residential Development</Overline>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold text-forest tracking-tight">
                We develop residential real estate — from raw land to community
              </h2>
              <p className="mt-6 text-ink text-lg leading-relaxed">
                We evaluate and advance residential opportunities based on market demand, infrastructure, zoning,
                and long-term value — working through entitlement and development to create lasting neighborhoods.
              </p>
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {DEV_POINTS.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-forest font-medium">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
                      <Check size={13} weight="bold" className="text-forest" />
                    </span>
                    {d}
                  </li>
                ))}
              </ul>
              <Link to="/development" className={`${btn.outline} mt-10`} data-testid="residential-development-btn">
                Our Development Approach <ArrowRight size={18} />
              </Link>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* INVESTMENT FOCUS */}
      <section className="relative bg-forest text-white py-20 md:py-32 overflow-hidden" data-testid="investment-focus">
        <img src={HOME_IMG.land} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-[0.12]" />
        <div className="grain" />
        <Container className="relative">
          <div className="max-w-2xl">
            <Reveal>
              <Overline>Our Investment Focus</Overline>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
                The opportunities we actively pursue
              </h2>
              <p className="mt-5 text-white/60 text-lg leading-relaxed">
                From undeveloped land to infill and assemblage, we evaluate a broad range of residential
                opportunities on their individual merits.
              </p>
            </Reveal>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
            {INVESTMENT_FOCUS.map((f, i) => (
              <Reveal key={f.title} delay={(i % 4) * 0.06}>
                <div className="h-full bg-forest p-8 hover:bg-forest-light transition-colors duration-300 group">
                  <span className="font-serif text-gold text-2xl">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-4 font-serif text-lg font-semibold text-white">{f.title}</h3>
                  <p className="mt-2 text-white/55 text-sm leading-relaxed">{f.desc}</p>
                  <span className="mt-4 block h-px w-0 bg-gold group-hover:w-10 transition-all duration-500" />
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* CHARLOTTE REGION — parallax band */}
      <section className="relative overflow-hidden" data-testid="region-band">
        <div className="absolute inset-0 bg-fixed bg-center bg-cover" style={{ backgroundImage: `url(${HOME_IMG.skyline})` }} aria-hidden />
        <div className="absolute inset-0 bg-forest/85" />
        <div className="grain" />
        <Container className="relative py-24 md:py-32 text-center">
          <Reveal>
            <Overline className="mb-5">Charlotte &amp; Surrounding NC Markets</Overline>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight max-w-3xl mx-auto text-balance">
              Focused on one of the fastest-growing regions in the country
            </h2>
            <p className="mt-6 text-white/65 text-lg leading-relaxed max-w-2xl mx-auto">
              Sustained population growth, infrastructure investment, and residential demand make the Charlotte
              metro a compelling place to acquire and develop land.
            </p>
          </Reveal>
        </Container>
      </section>

      {/* GEOGRAPHIC FOCUS */}
      <section className="py-20 md:py-32" data-testid="geographic-focus">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
            <Reveal>
              <Overline>Geographic Focus</Overline>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-forest tracking-tight">
                Where we evaluate opportunities
              </h2>
              <p className="mt-6 text-ink text-lg leading-relaxed">
                Redwood Capital concentrates on the Charlotte metro and surrounding high-growth corridors, where
                population growth, infrastructure investment, and residential demand continue to expand.
              </p>
              <ul className="mt-8 grid grid-cols-2 gap-y-3 gap-x-4">
                {MARKETS.map((m) => (
                  <li key={m} className="flex items-center gap-2.5 text-forest font-medium">
                    <MapPin size={18} weight="fill" className="text-gold shrink-0" />
                    {m}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="bg-white border border-line rounded-sm p-4 sm:p-8 shadow-[0_4px_24px_rgba(13,46,27,0.04)]">
                <StylizedMap />
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* WHY REDWOOD */}
      <section className="bg-white py-20 md:py-32 border-y border-line" data-testid="why-redwood">
        <Container>
          <Reveal>
            <Overline>Why Redwood Capital</Overline>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-forest tracking-tight max-w-2xl">
              Built on knowledge, discipline, and partnership
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
            {WHY_REDWOOD.map((w, i) => (
              <Reveal key={w.title} delay={(i % 2) * 0.1}>
                <div className="flex gap-5 group">
                  <div className="shrink-0 w-14 h-14 flex items-center justify-center rounded-sm bg-forest group-hover:bg-forest-light transition-colors duration-300">
                    <Icon name={w.icon} size={26} weight="light" className="text-gold" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-forest">{w.title}</h3>
                    <p className="mt-2 text-ink leading-relaxed">{w.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* WHO WE WORK WITH */}
      <section className="py-20 md:py-24" data-testid="who-we-work-with">
        <Container>
          <Reveal>
            <div className="rounded-sm border border-line bg-cream p-8 md:p-12">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
                <div className="max-w-2xl">
                  <Overline>Owners · Brokers · Builders · Investors</Overline>
                  <h2 className="mt-4 font-serif text-2xl sm:text-3xl font-semibold text-forest tracking-tight">
                    We work with the people who make development happen
                  </h2>
                  <p className="mt-4 text-ink leading-relaxed">
                    Landowners, land and commercial brokers, Realtors, builders, engineers, planners, and investment
                    partners — we welcome both listed and off-market opportunities.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row lg:flex-col gap-3 shrink-0">
                  <Link to="/submit-property" className={btn.gold} data-testid="work-submit-btn">
                    Submit a Property <ArrowRight size={18} />
                  </Link>
                  <Link to="/brokers-partners" className={btn.outline} data-testid="work-partner-btn">
                    Partner With Us
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* HAVE LAND TO SELL */}
      <section className="relative overflow-hidden bg-forest" data-testid="sell-your-land">
        <img src={HOME_IMG.land} alt="Undeveloped land with forest and open fields" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-forest via-forest/90 to-forest/60" />
        <div className="grain" />
        <Container className="relative py-20 md:py-28">
          <div className="max-w-3xl">
            <Reveal>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-gold">
                <span className="h-px w-8 bg-gold" /> Sell Your Land
              </span>
              <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight text-balance">
                Have Land to Sell?
              </h2>
              <p className="mt-6 text-white/75 text-lg lg:text-xl leading-relaxed max-w-2xl">
                Whether your property is currently listed, off-market, inherited, agricultural, or simply
                underutilized, we would like to learn more about it. Every opportunity is evaluated individually.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link to="/submit-property" className={`${btn.gold} shadow-[0_10px_30px_rgba(201,168,117,0.35)]`} data-testid="sell-land-submit-btn">
                  Submit Your Property <ArrowRight size={18} />
                </Link>
                <Link to="/contact" className={btn.outlineLight} data-testid="sell-land-contact-btn">
                  Contact Us
                </Link>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>
    </div>
  );
}
